import type { BrandConfig } from '@estimating/branding';
import type { Locale } from '@estimating/i18n';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { App as AntdApp, ConfigProvider, type ThemeConfig } from 'antd';
import type { i18n as I18n } from 'i18next';
import { useMemo, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { AuthenticatorContext } from '../../features/auth/auth-context';
import type { Authenticator } from '../../features/auth/authenticator';
import { ApiClientContext } from '../../shared/api/api-client-context';
import type { ApiClient } from '../../shared/api/api-client';
import { BrandContext } from '../brand-context';
import { useLocale } from '../locale/locale-context';
import { antdLocales } from '../locale/locale-packs';
import type { LocalePreferenceStore } from '../locale/locale-preference';
import { LocaleProvider } from './LocaleProvider';

export interface AppProvidersProps {
  readonly brand: BrandConfig;
  readonly i18n: I18n;
  readonly initialLocale: Locale;
  readonly preferences: LocalePreferenceStore;
  readonly apiClient: ApiClient;
  readonly authenticator: Authenticator;
  readonly queryClient: QueryClient;
  readonly children: ReactNode;
}

/** Maps brand-neutral tokens (D-021) onto Ant Design's theme. */
function toAntdTheme(brand: BrandConfig): ThemeConfig {
  return {
    cssVar: { key: `brand-${brand.id}` },
    token: {
      colorPrimary: brand.theme.colorPrimary,
      borderRadius: brand.theme.borderRadius,
    },
  };
}

function BrandedAntd({ brand, children }: { brand: BrandConfig; children: ReactNode }) {
  const { locale } = useLocale();
  const theme = useMemo(() => toAntdTheme(brand), [brand]);
  return (
    <ConfigProvider theme={theme} locale={antdLocales[locale]}>
      <AntdApp>{children}</AntdApp>
    </ConfigProvider>
  );
}

/** Everything a screen needs from context; dependencies are passed in, never imported as singletons. */
export function AppProviders({
  brand,
  i18n,
  initialLocale,
  preferences,
  apiClient,
  authenticator,
  queryClient,
  children,
}: AppProvidersProps) {
  return (
    <BrandContext value={brand}>
      <I18nextProvider i18n={i18n}>
        <LocaleProvider
          brand={brand}
          i18n={i18n}
          initialLocale={initialLocale}
          preferences={preferences}
        >
          <BrandedAntd brand={brand}>
            <AuthenticatorContext value={authenticator}>
              <ApiClientContext value={apiClient}>
                <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
              </ApiClientContext>
            </AuthenticatorContext>
          </BrandedAntd>
        </LocaleProvider>
      </I18nextProvider>
    </BrandContext>
  );
}
